import {
  IonList,
  IonListHeader,
  IonLabel,
  IonItem,
  IonModal,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonLoading,
} from '@ionic/react';
import { cameraOutline, pencilOutline } from 'ionicons/icons';
import { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../lib/context/AppContext';
import { useForm } from 'react-hook-form';
import { Camera, CameraResultType } from '@capacitor/camera';

export default function AccountInfoUpdateDetails() {
  const { apiService, user, updateUser } = useContext(AppContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ reValidateMode: 'onChange', mode: 'onChange' });
  const [editAccountModalIsOpen, setEditAccountModalIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [isLoading, setIsLoading] = useState(user);

  useEffect(() => {
    if (!editAccountModalIsOpen && !!user) {
      apiService?.get('/users/me?populate=address,picture').then((response) => {
        updateUser(response.data);
        setCurrentUser(response.data);
      });
    } else if (!user) {
      window.location.href = '/auth';
    }
  }, [apiService, editAccountModalIsOpen]);

  return (
    <>
      <IonLoading spinner='bubbles' isOpen={isLoading} />
      <IonList>
        <IonListHeader>
          <IonLabel>Account</IonLabel>
          <IonButton
            onClick={() => {
              setEditAccountModalIsOpen(true);
            }}
          >
            <IonIcon icon={pencilOutline} color='secondary' />
          </IonButton>
        </IonListHeader>
        <IonItem>
          <IonLabel>
            {currentUser?.picture?.url && (
              <img
                alt='Profile'
                src={currentUser?.picture?.url}
                className='w-20 h-20 rounded-full border-2 border-primary mb-4 object-cover'
              ></img>
            )}
            {currentUser?.firstName} {currentUser?.lastName}
            <div className='mt-2 text-neutral-500'>{currentUser?.email}</div>
            <div className='mt-2 text-neutral-500'>
              {currentUser?.address?.street || ''}{' '}
              {currentUser?.address?.city || ''}{' '}
              {currentUser?.address?.cap || ''}{' '}
              {currentUser?.address?.province || ''}
            </div>
          </IonLabel>
        </IonItem>
      </IonList>

      <IonModal isOpen={editAccountModalIsOpen}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot='start'>
              <IonButton onClick={() => setEditAccountModalIsOpen(false)}>
                Annulla
              </IonButton>
            </IonButtons>
            <IonTitle>Modifica info</IonTitle>
            <IonButtons slot='end'>
              <IonButton
                onClick={handleSubmit(async (data) => {
                  setIsLoading(true);
                  apiService
                    ?.put(`/users/${user.id}?populate=address,picture`, data)
                    .then((res) => {
                      setEditAccountModalIsOpen(false);
                    })
                    .finally(() => {
                      setIsLoading(false);
                    });
                })}
              >
                Conferma
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className='ion-padding'>
          <div className='flex items-center mb-8'>
            {currentUser?.picture?.url && (
              <img
                alt='Profile'
                src={currentUser.picture.url}
                className='w-20 h-20 rounded-full border-2 border-primary object-cover'
              ></img>
            )}
            {!currentUser?.picture?.url && (
              <IonIcon
                icon={cameraOutline}
                className='w-8 h-8 p-4 border-2 border-primary rounded-full'
              ></IonIcon>
            )}
            <IonButton
              className='ml-4'
              onClick={async () => {
                const image = await Camera.getPhoto({
                  quality: 90,
                  allowEditing: true,
                  resultType: CameraResultType.DataUrl,
                });
                if (!!image?.dataUrl) {
                  setIsLoading(true);
                  // Converti l'immagine Base64 in dati binari
                  const binaryData = atob(image?.dataUrl.split(',')[1]);
                  // Crea un array di byte dall'array di caratteri binari
                  const byteArray = new Uint8Array(binaryData.length);
                  for (let i = 0; i < binaryData.length; i++) {
                    byteArray[i] = binaryData.charCodeAt(i);
                  }
                  // Crea un oggetto Blob dai dati binari
                  const blob = new Blob([byteArray], { type: 'image/png' });
                  // Crea un oggetto FormData e aggiungi il blob
                  const formData = new FormData();
                  formData.append('files', blob, 'image.png');

                  formData.append('ref', 'plugin::users-permissions.user');
                  formData.append('refId', currentUser.id);
                  formData.append('field', 'picture');

                  const response = await apiService?.postForm(
                    '/upload',
                    formData
                  );

                  setCurrentUser({
                    ...currentUser,
                    picture: response?.data[0],
                  });

                  setIsLoading(false);
                }
              }}
            >
              Carica foto
            </IonButton>
          </div>

          <IonList lines='full'>
            <IonItem>
              <IonLabel position='floating'>Nome</IonLabel>
              <IonInput
                {...register('firstName')}
                value={user?.firstName}
                type='text'
                placeholder='Nome'
              />
            </IonItem>
            <IonItem>
              <IonLabel position='floating'>Cognome</IonLabel>
              <IonInput
                {...register('lastName')}
                value={user?.lastName}
                type='text'
                placeholder='Cognome'
              />
            </IonItem>
            <IonListHeader>
              <IonLabel>Indirizzo</IonLabel>
            </IonListHeader>
            <IonItem>
              <IonLabel position='floating'>Via</IonLabel>
              <IonInput
                {...register('address.street')}
                value={user?.address?.street}
                type='text'
                placeholder='Via'
              />
            </IonItem>
            <IonItem>
              <IonLabel position='floating'>Cap</IonLabel>
              <IonInput
                {...register('address.zip')}
                value={user?.address?.zip}
                type='text'
                placeholder='Cap'
              />
            </IonItem>
            <IonItem>
              <IonLabel position='floating'>Città</IonLabel>
              <IonInput
                {...register('address.city')}
                value={user?.address?.city}
                type='text'
                placeholder='Città'
              />
            </IonItem>
            <IonItem>
              <IonLabel position='floating'>Provincia</IonLabel>
              <IonInput
                {...register('address.province')}
                value={user?.address?.province}
                type='text'
                placeholder='Provincia'
              />
            </IonItem>
          </IonList>
        </IonContent>
      </IonModal>
    </>
  );
}
